import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import { logEmployeeActivity } from "../utils/activityLogger";
import "./Salary.css";

const formatMonth = (value) => value || "-";

const formatDayCount = (value) => {
  const number = Number(value || 0);
  return Number.isInteger(number) ? String(number) : number.toFixed(1);
};

const formatCurrency = (value) => {
  const number = Number(value || 0);
  if (Number.isNaN(number)) return "₹ 0.00";
  return `₹ ${number.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

const getCurrentMonth = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
};

const getCurrentDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
};

const normalizePayrollMonth = (value) => (value ? String(value).slice(0, 7) : "");
const SALARY_NET_WORTH_EVENT = "salary-networth-updated";
const SALARY_NET_WORTH_STORAGE_KEY = "salary_networth_updated_at";

const notifySalaryNetWorth = () => {
  window.dispatchEvent(new Event(SALARY_NET_WORTH_EVENT));
  localStorage.setItem(SALARY_NET_WORTH_STORAGE_KEY, String(Date.now()));
};

const clampMonthValue = (value) => {
  const normalizedValue = normalizePayrollMonth(value) || getCurrentMonth();
  return normalizedValue > getCurrentMonth() ? getCurrentMonth() : normalizedValue;
};

const getMonthRange = (monthValue) => {
  const normalizedMonth = normalizePayrollMonth(monthValue) || getCurrentMonth();
  const [yearValue, monthNumberValue] = normalizedMonth.split("-").map(Number);
  const year = Number.isInteger(yearValue) ? yearValue : new Date().getFullYear();
  const monthNumber = Number.isInteger(monthNumberValue) ? monthNumberValue : new Date().getMonth() + 1;
  const totalDays = new Date(year, monthNumber, 0).getDate();
  const month = String(monthNumber).padStart(2, "0");

  return {
    from: `${year}-${month}-01`,
    to: `${year}-${month}-${String(totalDays).padStart(2, "0")}`,
    totalDays,
  };
};

export default function Salary() {
  const navigate = useNavigate();
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [newSalary, setNewSalary] = useState({
    employee_id: "",
    month: "",
    deductions: "",
    advance: "",
    note: "",
  });
  const [monthInput, setMonthInput] = useState("");
  const [supportsMonthInput, setSupportsMonthInput] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth);
  const [employeeSalarySummary, setEmployeeSalarySummary] = useState(null);
  const [employeeSummaryLoading, setEmployeeSummaryLoading] = useState(false);
  const [previewPayableOverride, setPreviewPayableOverride] = useState("");
  const [isPreviewEditing, setIsPreviewEditing] = useState(false);

  useEffect(() => {
    fetchSalaries();
    fetchEmployees();
    const testInput = document.createElement("input");
    testInput.setAttribute("type", "month");
    setSupportsMonthInput(testInput.type === "month");
  }, []);

  const fetchSalaries = async () => {
    try {
      const res = await client.get("/salaries");
      setSalaries(res.data || []);
    } catch (err) {
      console.error("Fetch salaries error:", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await client.get("/users");
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Fetch employees error:", err);
    }
  };

  const fetchMonthlyAttendance = useCallback(async (monthValue) => {
    const { from, to } = getMonthRange(monthValue);

    try {
      const res = await client.get("/attendance", { params: { from, to } });
      setAttendanceRows(res.data || []);
    } catch (err) {
      console.error("Fetch monthly attendance error:", err);
      setAttendanceRows([]);
    }
  }, []);

  useEffect(() => {
    fetchMonthlyAttendance(selectedMonth);
  }, [fetchMonthlyAttendance, selectedMonth]);

  const handleMonthChange = (value) => {
    const normalized = value ? value.slice(0, 7) : "";
    const clampedValue = clampMonthValue(normalized);
    setMonthInput(clampedValue);
    setNewSalary((prev) => ({ ...prev, month: clampedValue }));
    if (clampedValue) setSelectedMonth(clampedValue);
    setPreviewPayableOverride("");
    setIsPreviewEditing(false);
  };

  const selectedEmployee = employees.find(
    (emp) => String(emp.id) === String(newSalary.employee_id)
  );
  const previewMonth = normalizePayrollMonth(newSalary.month) || selectedMonth || getCurrentMonth();
  const baseSalary = Number(selectedEmployee?.base_salary || 0);
  const deductionsValue = Number(newSalary.deductions || 0);
  const advanceValue = Number(newSalary.advance || 0);
  const calculatedNetPay = baseSalary - deductionsValue - advanceValue;
  const previewPayableAmount = Number(
    previewPayableOverride || employeeSalarySummary?.current_total_salary_generated || 0
  );

  const fetchEmployeeSalarySummary = useCallback(async (employeeId, monthValue) => {
    if (!employeeId) {
      setEmployeeSalarySummary(null);
      return;
    }

    try {
      setEmployeeSummaryLoading(true);
      const res = await client.get(`/salaries/employee/${employeeId}/summary`, {
        params: { month: normalizePayrollMonth(monthValue) || getCurrentMonth() },
      });
      setEmployeeSalarySummary(res.data || null);
      setPreviewPayableOverride("");
      setIsPreviewEditing(false);
    } catch (err) {
      console.error("Fetch employee salary summary error:", err);
      setEmployeeSalarySummary(null);
    } finally {
      setEmployeeSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!newSalary.employee_id) {
      setEmployeeSalarySummary(null);
      return;
    }

    fetchEmployeeSalarySummary(newSalary.employee_id, previewMonth);
  }, [
    fetchEmployeeSalarySummary,
    newSalary.employee_id,
    previewMonth,
    selectedEmployee?.base_salary,
  ]);

  const handleEmployeeChange = (employeeId) => {
    setNewSalary((prev) => ({ ...prev, employee_id: employeeId }));
    setPreviewPayableOverride("");
    setIsPreviewEditing(false);
  };

  const refreshEmployees = async () => {
    await fetchEmployees();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newSalary.employee_id || !newSalary.month) {
      alert("Employee and Month are required.");
      return;
    }
    if (!selectedEmployee) {
      alert("Select a valid employee.");
      return;
    }

    const payload = {
      employee_id: Number(newSalary.employee_id),
      month: newSalary.month,
      base_salary: baseSalary,
      deductions: deductionsValue,
      advance_deduction: advanceValue,
      net_pay: calculatedNetPay,
      note: newSalary.note || "",
      status: "Pending",
      manual_payable_amount: previewPayableAmount,
      status: "Paid",
    };

    setSaving(true);
    try {
      await client.post("/salaries", payload);
      void logEmployeeActivity({
        targetEmployeeId: Number(newSalary.employee_id),
        targetEmployeeName: selectedEmployee?.name || `Employee #${newSalary.employee_id}`,
        action: "Salary Entry Created",
        module: "Salary",
        description: `Created salary entry for ${selectedEmployee?.name || `Employee #${newSalary.employee_id}`} for ${newSalary.month}.`,
      });
      setNewSalary({
        employee_id: "",
        month: "",
        deductions: "",
        advance: "",
        note: "",
      });
      setMonthInput("");
      setEmployeeSalarySummary(null);
      setPreviewPayableOverride("");
      setIsPreviewEditing(false);
      notifySalaryNetWorth();
      fetchSalaries();
      alert("Salary entry added successfully!");
    } catch (err) {
      console.error("Add salary error:", err);
      alert("Failed to save salary entry.");
    } finally {
      setSaving(false);
    }
  };

  const monthPayrollRecords = useMemo(
    () =>
      salaries.filter(
        (salary) => normalizePayrollMonth(salary.month) === selectedMonth
      ),
    [salaries, selectedMonth]
  );

  const payrollByEmployee = useMemo(() => {
    const rowsByEmployee = new Map();

    monthPayrollRecords.forEach((salary) => {
      const employeeId = String(salary.employee_id);
      if (!rowsByEmployee.has(employeeId)) {
        rowsByEmployee.set(employeeId, salary);
      }
    });

    return rowsByEmployee;
  }, [monthPayrollRecords]);

  const payrollDashboardRows = useMemo(
    () => {
      const { totalDays } = getMonthRange(selectedMonth);
      const presentDaysByEmployee = new Map();

      attendanceRows.forEach((attendance) => {
        if (String(attendance.status || "").toLowerCase() !== "present") return;

        const employeeId = String(attendance.employee_id);
        presentDaysByEmployee.set(
          employeeId,
          (presentDaysByEmployee.get(employeeId) || 0) + 1
        );
      });

      return employees.map((employee) => {
        const payroll = payrollByEmployee.get(String(employee.id));
        const presentDays = presentDaysByEmployee.get(String(employee.id)) || 0;

        return {
          ...(payroll || {}),
          id: payroll?.id || `pending-${employee.id}-${selectedMonth}`,
          employee_id: employee.id,
          employee_name: payroll?.employee_name || employee.name,
          month: selectedMonth,
          present_days: presentDays,
          absent_days: Math.max(totalDays - presentDays, 0),
          payrollStatus: payroll ? "Paid" : "Pending",
        };
      });
    },
    [attendanceRows, employees, payrollByEmployee, selectedMonth]
  );

  const summary = useMemo(() => {
    const paidEmployees = payrollByEmployee.size;
    const totalEmployees = employees.length;

    return {
      totalEmployees,
      paidEmployees,
      pendingEmployees: Math.max(totalEmployees - paidEmployees, 0),
      processedPayroll: paidEmployees,
    };
  }, [employees.length, payrollByEmployee]);

  const filteredPayrollRows = useMemo(() => {
    switch (activeFilter) {
      case "paid":
        return payrollDashboardRows.filter((row) => row.payrollStatus === "Paid");
      case "pending":
        return payrollDashboardRows.filter((row) => row.payrollStatus === "Pending");
      case "processed":
        return payrollDashboardRows.filter((row) => row.payrollStatus === "Paid");
      case "all":
      default:
        return payrollDashboardRows;
    }
  }, [payrollDashboardRows, activeFilter]);

  return (
    <div className="salary-page page-container">
      <header className="salary-header">
        <div className="header-left">
          <div className="header-logo">
            <div className="logo-square">
              <i className="fa fa-money" aria-hidden="true"></i>
            </div>
          </div>
          <div>
            <h1 className="header-title">Salary Management</h1>
            <p className="header-subtitle">Payroll privacy with automatic net salary calculation.</p>
          </div>
        </div>
        <div className="header-actions">
          <button type="button" className="btn btn-outline" onClick={() => navigate("/salary-history")}>
            <Eye size={18} strokeWidth={2} />
            <span>View History</span>
          </button>
          <button type="button" className="btn btn-primary" onClick={() => alert("PDF export coming soon")}>
            <Download size={18} strokeWidth={2} />
            <span>Export PDF</span>
          </button>
        </div>
      </header>

      <div className="salary-main-card">
        <div className="filter-section">
          <div className="filter-controls">
            <div className="filter-group">
              <label className="filter-label">Filter by Month</label>
              <input
                type="month"
                value={selectedMonth}
                max={getCurrentMonth()}
                onChange={(e) => setSelectedMonth(clampMonthValue(e.target.value || getCurrentMonth()))}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Filter by Status</label>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="filter-input"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {selectedMonth && (
              <button
                type="button"
                className="filter-reset-btn"
                onClick={() => {
                  setSelectedMonth(getCurrentMonth());
                  setActiveFilter("all");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="payroll-stat-cards">
          <div
            className={`payroll-stat-card ${activeFilter === "all" ? "payroll-stat-card-active" : ""}`}
            onClick={() => setActiveFilter("all")}
            role="button"
            tabIndex="0"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setActiveFilter("all");
              }
            }}
          >
            <div className="payroll-stat-top">
              <div className="payroll-stat-value">{summary.totalEmployees}</div>
              <i className="fa fa-users payroll-stat-icon" aria-hidden="true"></i>
            </div>
            <div className="payroll-stat-label">Employees</div>
          </div>
          <div
            className={`payroll-stat-card ${activeFilter === "paid" ? "payroll-stat-card-active" : ""}`}
            onClick={() => setActiveFilter("paid")}
            role="button"
            tabIndex="0"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setActiveFilter("paid");
              }
            }}
          >
            <div className="payroll-stat-top">
              <div className="payroll-stat-value">{summary.paidEmployees}</div>
              <i className="fa fa-check-circle payroll-stat-icon" aria-hidden="true"></i>
            </div>
            <div className="payroll-stat-label">Paid</div>
          </div>
          <div
            className={`payroll-stat-card ${activeFilter === "pending" ? "payroll-stat-card-active" : ""}`}
            onClick={() => setActiveFilter("pending")}
            role="button"
            tabIndex="0"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setActiveFilter("pending");
              }
            }}
          >
            <div className="payroll-stat-top">
              <div className="payroll-stat-value">{summary.pendingEmployees}</div>
              <i className="fa fa-clock-o payroll-stat-icon" aria-hidden="true"></i>
            </div>
            <div className="payroll-stat-label">Pending</div>
          </div>
          <div
            className={`payroll-stat-card ${activeFilter === "processed" ? "payroll-stat-card-active" : ""}`}
            onClick={() => setActiveFilter("processed")}
            role="button"
            tabIndex="0"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setActiveFilter("processed");
              }
            }}
          >
            <div className="payroll-stat-top">
              <div className="payroll-stat-value">{summary.processedPayroll}</div>
              <i className="fa fa-money payroll-stat-icon" aria-hidden="true"></i>
            </div>
            <div className="payroll-stat-label">Payroll</div>
          </div>
        </div>

        <div className="add-entry-section">
          <h2 className="section-title">ADD NEW ENTRY</h2>
          <form onSubmit={handleSubmit} className="add-entry-form">
            <div className="form-field">
              <label className="field-label">EMPLOYEE</label>
              <div className="salary-input-with-icon">
                <select
                  name="employee_id"
                  value={newSalary.employee_id}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  className="salary-form-input"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">MONTH</label>
              <input
                type={supportsMonthInput ? "month" : "date"}
                name="month"
                value={monthInput}
                max={supportsMonthInput ? getCurrentMonth() : getCurrentDate()}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="salary-form-input"
                required
              />
            </div>

            <div className="form-field">
              <label className="field-label">DEDUCTIONS (₹)</label>
              <input
                type="number"
                name="deductions"
                value={newSalary.deductions}
                onChange={(e) => setNewSalary((prev) => ({ ...prev, deductions: e.target.value }))}
                className="salary-form-input"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-field">
              <label className="field-label">ADVANCE (₹)</label>
              <input
                type="number"
                name="advance"
                value={newSalary.advance}
                onChange={(e) => setNewSalary((prev) => ({ ...prev, advance: e.target.value }))}
                className="salary-form-input"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-field">
              <label className="field-label">NOTE</label>
              <textarea
                name="note"
                value={newSalary.note}
                onChange={(e) => setNewSalary((prev) => ({ ...prev, note: e.target.value }))}
                className="salary-form-input"
                rows={2}
                placeholder="Optional remark"
              />
            </div>

            <div className="form-field form-field-button">
              <button type="submit" className="btn btn-add-entry" disabled={saving || calculatedNetPay < 0}>
                {saving ? "Saving..." : "Add Entry"}
              </button>
            </div>
          </form>

          {newSalary.employee_id && (
            <div className="employee-salary-summary-wrap">
              <div className="employee-salary-summary-top">
                <span className="employee-salary-summary-month">
                  Preview Month: {previewMonth}
                </span>
                <span
                  className={`employee-salary-status employee-salary-status-${String(
                    employeeSalarySummary?.payment_status || "Pending"
                  ).toLowerCase()}`}
                >
                  {employeeSalarySummary?.payment_status || "Pending"}
                </span>
              </div>
              {employeeSalarySummary?.has_previous_pending_salary && (
                <div className="employee-salary-warning">
                  Pending salary for{" "}
                  {(employeeSalarySummary?.previous_pending_salary_months || []).join(", ")}
                </div>
              )}
              <div className="employee-salary-summary">
              {employeeSummaryLoading ? (
                <div className="employee-salary-summary-loading">Loading employee summary...</div>
              ) : (
                <>
                  <div className="employee-salary-summary-card">
                    <div className="employee-salary-summary-value">
                      {formatDayCount(employeeSalarySummary?.total_present_days)}
                    </div>
                    <div className="employee-salary-summary-label">Present Days</div>
                  </div>
                  <div className="employee-salary-summary-card">
                    <div className="employee-salary-summary-value">
                      {formatDayCount(employeeSalarySummary?.total_absent_days)}
                    </div>
                    <div className="employee-salary-summary-label">Absent Days</div>
                  </div>
                  <div className="employee-salary-summary-card">
                    <div className="employee-salary-summary-value">
                      {formatDayCount(employeeSalarySummary?.working_days_in_month)}
                    </div>
                    <div className="employee-salary-summary-label">Working Days in Month</div>
                  </div>
                  <div className="employee-salary-summary-card">
                    <div className="employee-salary-summary-label">Your Salary</div>
                    <div className="employee-salary-summary-value">
                      {formatCurrency(employeeSalarySummary?.base_salary ?? selectedEmployee?.base_salary ?? 0)}
                    </div>
                    <div className="employee-salary-summary-label">Payable Salary</div>
                    <div className="employee-salary-summary-value">
                      {formatCurrency(previewPayableAmount)}
                    </div>
                    <div className="employee-salary-summary-label">Calculated Salary</div>
                    <div className="employee-salary-summary-value">
                      {formatCurrency(employeeSalarySummary?.current_total_salary_generated ?? 0)}
                    </div>
                    <button
                      type="button"
                      className="salary-inline-save-btn"
                      onClick={() => setIsPreviewEditing((prev) => !prev)}
                      style={{ marginTop: "0.75rem" }}
                    >
                      {isPreviewEditing ? "Cancel" : "Edit"}
                    </button>
                    {isPreviewEditing && (
                      <div style={{ marginTop: "0.75rem" }}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={previewPayableOverride}
                          onChange={(e) => setPreviewPayableOverride(e.target.value)}
                          className="salary-form-input"
                          placeholder="Enter payable salary"
                        />
                        <button
                          type="button"
                          className="salary-inline-save-btn"
                          onClick={() => {
                            setIsPreviewEditing(false);
                            if (previewPayableOverride === "") {
                              setPreviewPayableOverride("");
                            }
                          }}
                          style={{ marginTop: "0.5rem" }}
                        >
                          Save Override
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
              </div>
            </div>
          )}
        </div>

        <div className="table-section">
          <h2 className="section-title">PAYROLL DASHBOARD</h2>
          <div className="table-wrapper">
            <table className="salary-table payroll-dashboard-table">
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Month</th>
                  <th>Present Days</th>
                  <th>Absent Days</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayrollRows.length > 0 ? (
                  filteredPayrollRows.map((salary) => (
                    <tr key={salary.id}>
                      <td>
                        <div className="employee-cell">
                          <i className="fa fa-user-circle employee-profile-icon" aria-hidden="true"></i>
                          <span className="employee-name">{salary.employee_name || "-"}</span>
                        </div>
                      </td>
                      <td>{formatMonth(salary.month)}</td>
                      <td>{formatDayCount(salary.present_days)}</td>
                      <td>{formatDayCount(salary.absent_days)}</td>
                      <td>
                        <span className={`status-badge status-${salary.payrollStatus.toLowerCase()}`}>
                          {salary.payrollStatus}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="empty-row">
                      No payroll entries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
}
